// LicensesPage - what every client has been allocated, what they are
// using, and where that gap costs somebody money.
//
// FOUR NUMBERS, AND THEY ARE NOT THE SAME THING. Conflating any two is
// how a licence report stops being worth reading:
//
//   allocated  seats sold to this client
//   assigned   people given a console account
//   active     endpoints reporting in the last 30 days
//   dormant    endpoints that once reported and have gone quiet
//
// A client with 25 allocated, 5 assigned and 0 active is what a real
// estate looks like when licences were bought and the extension was
// never rolled out. That gap is the finding - a report showing one
// number would hide it, and the gap is what an MSP acts on.
//
// UNLICENSED IS NOT ZERO SEATS. A client with no plan row has not been
// sold anything yet; a client with zero seats has. Showing both as 0
// would put a provisioning gap and a commercial fact in the same cell.
import { useEffect, useState } from 'react';
import { CreditCard, Users, Monitor, AlertTriangle, Download } from 'lucide-react';
import { listLicenseSummary } from '../lib/adminApi.js';
import { EmptyState } from '@govern360/shared/components/ui.jsx';

export default function LicensesPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [focus, setFocus] = useState(null);
  const [sortKey, setSortKey] = useState('seats_allocated');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    let live = true;
    listLicenseSummary().then(({ data, error }) => {
      if (!live) return;
      if (error) setErr(error.message || 'Could not load licence summary');
      setRows(data || []);
      setLoading(false);
    });
    return () => { live = false; };
  }, []);

  const licensed = rows.filter((r) => r.licensed);
  const totalAllocated = licensed.reduce((s, r) => s + (r.seats_allocated || 0), 0);
  const totalAssigned = rows.reduce((s, r) => s + (r.seats_assigned || 0), 0);
  const totalActive = rows.reduce((s, r) => s + (r.endpoints_active || 0), 0);
  const overLimit = rows.filter((r) => r.overage || r.near_limit).length;
  const dormantTotal = rows.reduce((s, r) => s + (r.endpoints_dormant || 0), 0);

  function sorted() {
    const f = focus === 'overage' ? rows.filter((r) => r.overage || r.near_limit)
            : focus === 'unlicensed' ? rows.filter((r) => !r.licensed)
            : focus === 'dormant' ? rows.filter((r) => (r.endpoints_dormant || 0) > 0)
            : rows;
    const dir = sortDir === 'asc' ? 1 : -1;
    return f.slice().sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      if (typeof av === 'string') return av.localeCompare(bv) * dir;
      return (av - bv) * dir;
    });
  }

  function th(key, label) {
    return (
      <th style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
        onClick={() => { if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc'); else { setSortKey(key); setSortDir('desc'); } }}>
        {label}{sortKey === key ? (sortDir === 'asc' ? ' \u2191' : ' \u2193') : ''}
      </th>
    );
  }

  function exportCsv() {
    const cell = (v) => {
      if (v === null || v === undefined) return '';
      const s = String(v);
      return /[",\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
    };
    const lines = [];
    lines.push(cell('Govern360 - licence and usage summary per client. Allocated is seats sold; assigned is console accounts; active is endpoints reporting within 30 days; dormant is endpoints silent for 30 days or more. Exported '
      + new Date().toISOString().slice(0, 19).replace('T', ' ') + ' UTC.'));
    lines.push(['Client', 'Plan', 'Status', 'Allocated', 'Assigned', 'Utilisation %',
      'Endpoints active', 'Endpoints dormant', 'Endpoints ever', 'Over limit', 'Near limit', 'Licensed']
      .map(cell).join(','));
    for (const r of sorted()) {
      lines.push([r.tenant_name, r.tier_key, r.plan_status, r.seats_allocated, r.seats_assigned,
        r.utilisation_pct, r.endpoints_active, r.endpoints_dormant, r.endpoints_ever,
        r.overage ? 'yes' : 'no', r.near_limit ? 'yes' : 'no', r.licensed ? 'yes' : 'no']
        .map(cell).join(','));
    }
    const blob = new Blob([String.fromCharCode(0xFEFF) + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const el = document.createElement('a');
    el.href = url;
    el.download = 'govern360-licence-usage-' + new Date().toISOString().slice(0, 10) + '.csv';
    el.click();
    URL.revokeObjectURL(url);
  }

  const tile = (key, Icon, label, value, sub, tone) => {
    const on = focus === key;
    return (
      <div className="card" role={key ? 'button' : undefined} tabIndex={key ? 0 : undefined}
        onClick={() => key && setFocus(on ? null : key)}
        style={{ cursor: key ? 'pointer' : 'default',
          outline: on ? '2px solid var(--g-primary)' : 'none', outlineOffset: -2 }}>
        <p className="metric-l"><Icon size={16} />{label}</p>
        <p className="metric-v" style={{ color: tone }}>{value}</p>
        <p className="metric-d nu">{sub}</p>
      </div>
    );
  };

  return (
    <div>
      <div className="ph">
        <div>
          <h1 className="ph-h1">Licences &amp; usage</h1>
          <p className="ph-sub">Allocated, assigned and in use across every client. No manual reports.</p>
        </div>
        <div className="ph-cta">
          <button className="btn outline" onClick={exportCsv} disabled={!rows.length}>
            <Download size={16} />Export for billing</button>
        </div>
      </div>

      {err && (
        <div className="banner warn" style={{ marginBottom: 14 }}>
          <AlertTriangle size={16} /><span>{err}</span>
        </div>
      )}

      {loading ? (
        <div className="card"><p style={{ color: 'var(--g-text-2)' }}>Loading...</p></div>
      ) : rows.length === 0 ? (
        <EmptyState icon={CreditCard} title="No client tenants"
          hint="Provision a client organisation to see licence and usage figures." />
      ) : (
        <>
          <div className="row r4" style={{ marginBottom: 16 }}>
            {tile(null, CreditCard, 'Seats allocated', totalAllocated,
              licensed.length + ' licensed client(s)', undefined)}
            {tile(null, Users, 'Seats assigned', totalAssigned,
              totalAllocated > 0 ? Math.round(100 * totalAssigned / totalAllocated) + '% of allocated' : 'no allocation recorded', undefined)}
            {tile('dormant', Monitor, 'Endpoints active', totalActive,
              dormantTotal > 0 ? dormantTotal + ' dormant 30+ days' : 'reporting in last 30 days',
              totalActive === 0 ? 'var(--g-warning)' : undefined)}
            {tile('overage', AlertTriangle, 'At or over limit', overLimit,
              'clients at 90% or above', overLimit ? 'var(--g-warning)' : undefined)}
          </div>

          <div className="card pad-0">
            <div style={{ overflowX: 'auto' }}>
              <table className="tb">
                <thead><tr>
                  {th('tenant_name', 'Client')}
                  {th('tier_key', 'Plan')}
                  {th('seats_allocated', 'Allocated')}
                  {th('seats_assigned', 'Assigned')}
                  {th('utilisation_pct', 'Utilisation')}
                  {th('endpoints_active', 'Endpoints active')}
                  {th('endpoints_dormant', 'Dormant')}
                  <th>Status</th>
                </tr></thead>
                <tbody>
                  {sorted().map((r) => (
                    <tr key={r.tenant_id}>
                      <td><strong>{r.tenant_name}</strong></td>
                      <td style={{ color: 'var(--g-text-2)' }}>{r.tier_key || '\u2014'}</td>
                      {/* An unlicensed client has not been sold seats. That is
                          not the same as being sold zero, and a dash says so. */}
                      <td>{r.licensed ? r.seats_allocated : '\u2014'}</td>
                      <td>{r.seats_assigned}</td>
                      <td style={{ color: r.overage ? 'var(--g-danger)' : r.near_limit ? 'var(--g-warning)' : undefined }}>
                        {r.utilisation_pct === null || r.utilisation_pct === undefined ? '\u2014' : r.utilisation_pct + '%'}</td>
                      <td style={{ color: (r.endpoints_active === 0 && r.seats_assigned > 0) ? 'var(--g-warning)' : undefined }}>
                        {r.endpoints_active}</td>
                      <td style={{ color: r.endpoints_dormant > 0 ? 'var(--g-warning)' : undefined }}>
                        {r.endpoints_dormant || '\u2014'}</td>
                      <td>
                        {!r.licensed ? <span className="pill p-gray">Unlicensed</span>
                          : r.overage ? <span className="pill p-dang">Over limit</span>
                          : r.near_limit ? <span className="pill p-warn">Near limit</span>
                          : <span className="pill p-succ">Within limit</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Dormancy is reported, never reclaimed. Harvesting a seat from
              somebody on leave is worse than carrying it for a month. */}
          <p style={{ fontSize: 11, color: 'var(--g-text-3)', marginTop: 12, lineHeight: 1.6 }}>
            Allocated is what the client has been sold. Assigned counts console accounts. Active counts
            endpoints reporting within 30 days; dormant counts those silent for 30 days or more and is
            shown for review rather than reclaimed automatically. A client showing assigned seats and no
            active endpoints has licences in place and nothing deployed &mdash; which is a conversation,
            not a billing error.
          </p>
        </>
      )}
    </div>
  );
}
